import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmployeeAvatar } from "./EmployeeAvatar";
import { formatDepartment, type OrgEmployee } from "./types";

interface OrgListViewProps {
  employees: OrgEmployee[];
  managerById: Map<string, OrgEmployee>;
  onSelect: (employee: OrgEmployee) => void;
}

export function OrgListView({ employees, managerById, onSelect }: OrgListViewProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "fullName", desc: false }]);

  const columns = useMemo<ColumnDef<OrgEmployee>[]>(
    () => [
      {
        accessorKey: "fullName",
        header: "Nome",
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <EmployeeAvatar
              fullName={row.original.fullName}
              photoUrl={row.original.slackPhotoUrl}
              size={32}
            />
            <span className="font-medium">{row.original.fullName}</span>
          </div>
        ),
      },
      {
        accessorKey: "jobTitle",
        header: "Função",
        cell: ({ row }) => row.original.jobTitle ?? "—",
      },
      {
        accessorKey: "department",
        header: "Área",
        cell: ({ row }) => formatDepartment(row.original.department),
      },
      {
        id: "manager",
        header: "Gestor",
        accessorFn: (e) =>
          e.directManagerId ? (managerById.get(e.directManagerId)?.fullName ?? "") : "",
        cell: ({ row }) => {
          const m = row.original.directManagerId
            ? managerById.get(row.original.directManagerId)
            : null;
          return m?.fullName ?? "—";
        },
      },
    ],
    [managerById],
  );

  const table = useReactTable({
    data: employees,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="rounded-md border bg-card shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead key={header.id}>
                    {header.column.getCanSort() ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8"
                        onClick={() => header.column.toggleSorting(sorted === "asc")}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === "asc" ? (
                          <ArrowUp className="ml-2 h-3 w-3" />
                        ) : sorted === "desc" ? (
                          <ArrowDown className="ml-2 h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="ml-2 h-3 w-3 opacity-50" />
                        )}
                      </Button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                Nenhum colaborador encontrado.
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className="cursor-pointer"
                onClick={() => onSelect(row.original)}
                data-testid={`row-org-${row.original.id}`}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
